import React, { createContext } from 'react';
import { useHistory } from 'react-router-dom';

import { NumberParam } from 'use-query-params';
import { useQueryParamWithHistory } from '../hooks/useQueryParamWithHistory';

import { SESSION_TEAM_KEY, SESSION_USER_EMAIL } from '../constants/localStorage';
import { InitContext, InitCtx } from './InitProvider';
import { readStoredTeamId, userFingerprint } from './storedTeam';

type Props = {
  children: React.ReactElement;
};
type SetTeamCbFn = () => void;
type SetTeamFn = (teamId: number, cb?: SetTeamCbFn) => void;
type TeamCtx = {
  teamId: number;
  setTeam: SetTeamFn;
};

/**
 * The selected team is a USER preference, not session state. Keying it to the session `expires_at`
 * meant every re-login discarded it, dropping you onto someone else's portfolio via the `teams[0]`
 * fallback in `useInitialQuery`. Keyed to the signed-in user, it survives re-login instead.
 */
function getLocalStorageTeamId(): number {
  try {
    const { teamId, discard } = readStoredTeamId(
      localStorage.getItem(SESSION_TEAM_KEY),
      localStorage.getItem(SESSION_USER_EMAIL),
    );
    if (discard) localStorage.removeItem(SESSION_TEAM_KEY);
    return teamId;
  } catch {
    // eslint-disable-next-line no-console
    console.warn('Error parsing the local storage team');
  }

  return 0;
}

function setLocalStorageTeamId(id: number) {
  // Never persist a team with no identity attached: readStoredTeamId short-circuits on a falsy
  // `userRef`, so such a record could never be discarded by anyone. Leaving the previous record
  // in place is the safe miss — it belongs to a known user and is still discardable.
  const userRef = userFingerprint(localStorage.getItem(SESSION_USER_EMAIL));
  if (!userRef) return;

  localStorage.setItem(SESSION_TEAM_KEY, JSON.stringify({ teamId: id, userRef }));
}

export const localStorageTeamId = getLocalStorageTeamId();
const TeamContext = createContext<TeamCtx>({
  teamId: localStorageTeamId,

  setTeam() {},
});

function TeamProvider(props: Props) {
  const historyInner = useHistory();
  const { loggedIn } = React.useContext<InitCtx>(InitContext);
  const [teamId, setTeamId] = React.useState(localStorageTeamId);
  const [urlTeamId] = useQueryParamWithHistory('team_id', NumberParam);

  // Ref to track current team ID for the history listener (avoids stale closures)
  const teamIdRef = React.useRef(localStorageTeamId);

  // Sync team from URL parameter (initial load, shared links, direct URL access).
  // Uses teamIdRef instead of teamId to avoid overriding intentional setTeam() calls.
  React.useEffect(() => {
    console.log('[TeamProvider] URL sync effect:', {
      urlTeamId,
      teamIdRef: teamIdRef.current,
      willSync: urlTeamId && urlTeamId !== teamIdRef.current,
    });
    if (urlTeamId && urlTeamId !== teamIdRef.current) {
      teamIdRef.current = urlTeamId;
      setTeamId(urlTeamId);
      setLocalStorageTeamId(urlTeamId);
    }
  }, [urlTeamId]);

  // The read at module scope ran on the /callback document, where `login()` has cleared
  // `user_email` and `setSession` has not yet rewritten it — so it could not tell a returning user
  // from a different one, and a shared browser inherited the previous person's team. Re-consult
  // once identity exists. Runs after the URL sync above, so an explicit ?team_id= still wins.
  React.useLayoutEffect(() => {
    if (!loggedIn) return;
    const storedTeamId = getLocalStorageTeamId();
    if (storedTeamId === teamIdRef.current) return;
    teamIdRef.current = storedTeamId;
    setTeamId(storedTeamId);
  }, [loggedIn]);

  // Uses history.replace() directly instead of use-query-params' setValue, because setValue reads
  // refLocation.current, which is STALE inside a history.listen callback.
  React.useEffect(() => {
    const unlisten = historyInner.listen((location, action) => {
      if (action === 'POP') {
        const params = new URLSearchParams(location.search);
        const urlTeam = Number(params.get('team_id'));
        console.log('[TeamProvider] POP:', {
          search: location.search,
          urlTeam,
          teamIdRef: teamIdRef.current,
          willRestore: urlTeam && urlTeam !== teamIdRef.current,
        });
        if (urlTeam && urlTeam !== teamIdRef.current) {
          teamIdRef.current = urlTeam;
          setTeamId(urlTeam);
          setLocalStorageTeamId(urlTeam);
        }
      } else {
        const params = new URLSearchParams(location.search);
        const urlTeam = Number(params.get('team_id'));
        console.log('[TeamProvider] history.listen:', {
          action,
          search: location.search,
          urlTeam,
          teamIdRef: teamIdRef.current,
          willAddTeamId: urlTeam !== teamIdRef.current,
        });
        if (urlTeam !== teamIdRef.current) {
          // Use history.replace directly with the listener's location (which is fresh).
          // Do NOT use use-query-params' setValue here — it reads from stale React context.
          const newParams = new URLSearchParams(location.search);
          newParams.set('team_id', String(teamIdRef.current));
          historyInner.replace(`${location.pathname}?${newParams.toString()}`);
        }
      }
    });
    return unlisten;
  }, [historyInner]);

  const setTeam = React.useCallback((id: number, cb?: SetTeamCbFn) => {
    console.log('[TeamProvider] setTeam:', { newId: id, hasCallback: !!cb });
    setLocalStorageTeamId(id);
    setTeamId(id);
    teamIdRef.current = id;

    if (cb) {
      cb();
    }
  }, []);

  const values = React.useMemo(
    () => ({
      teamId,
      setTeam,
    }),
    [teamId, setTeam],
  );

  return <TeamContext.Provider value={values} {...props} />;
}

function useTeam() {
  const context = React.useContext(TeamContext);
  if (context === undefined) {
    throw new Error(`useTeam must be used within TeamProvider`);
  }

  return context;
}

export default TeamProvider;
export { TeamContext, useTeam };
export type { SetTeamFn, SetTeamCbFn, TeamCtx };
